import type { OccurrenceItem, RecurringTemplate } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { formatDay } from '../lib/dates';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { formatAmount } from '../lib/money';
import { FormError } from './FormError';
import { Button } from './ui/button';
import { IconSquare } from './ui/card';

/** Where "Record" goes: Add, pre-filled with the template, for that due date. */
export function recordLink(template: RecurringTemplate, dueOn: string): string {
  const query = new URLSearchParams({ kind: template.kind, recurring: template.id, due: dueOn });
  return `/add?${query.toString()}`;
}

/** One open occurrence per entry, overdue first, then by due date (for Due soon). */
export function dueOccurrences(
  templates: readonly RecurringTemplate[],
): Array<{ template: RecurringTemplate; occurrence: OccurrenceItem }> {
  return templates
    .flatMap((template) => template.due.map((occurrence) => ({ template, occurrence })))
    .sort((a, b) => {
      const late =
        (b.occurrence.state === 'overdue' ? 1 : 0) - (a.occurrence.state === 'overdue' ? 1 : 0);
      return late || a.occurrence.dueOn.localeCompare(b.occurrence.dueOn);
    });
}

/**
 * A due occurrence in a list: name, due date and account, the planned amount (or none when it
 * changes every time), state; "Record" opens Add pre-filled, "Skip" asks first. On the newest
 * missed occurrence of a template with several missed, "Skip all older" skips the others.
 */
export function RecurringDueRow({
  template,
  occurrence,
  today,
}: {
  template: RecurringTemplate;
  occurrence: OccurrenceItem;
  today: string;
}) {
  const { t, i18n } = useTranslation();
  const invalidate = useInvalidateLedger();
  const [error, setError] = useState<unknown>(null);
  const day = (date: string) =>
    formatDay(date, today, i18n.language, {
      today: t('activity.today'),
      yesterday: t('activity.yesterday'),
    });
  const overdue = template.due.filter((o) => o.state === 'overdue');
  const newestOverdue = overdue[overdue.length - 1];
  const older = occurrence === newestOverdue ? overdue.length - 1 : 0;
  const skip = useMutation({
    mutationFn: (mode: 'one' | 'older') =>
      mode === 'one'
        ? api.recurring.skip(template.id, occurrence.dueOn)
        : api.recurring.skipOlder(template.id, occurrence.dueOn),
    onSuccess: invalidate,
    onError: setError,
  });
  const late = occurrence.state === 'overdue';
  return (
    <div className="flex flex-col gap-1 px-4 py-3">
      <div className="flex items-center gap-3">
        <IconSquare>
          <CalendarClock className="size-5" strokeWidth={2} />
        </IconSquare>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[15px] font-medium">{template.name}</span>
          <span className="truncate text-xs text-muted">
            {day(occurrence.dueOn)} · {template.account.name}
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end">
          {template.amount && (
            <span dir="ltr" className="text-[15px] font-semibold tabular-nums">
              {formatAmount(template.amount, template.currency, i18n.language)}
            </span>
          )}
          <span className={late ? 'text-xs font-semibold text-expense' : 'text-xs text-muted'}>
            {t(`debts.states.${occurrence.state === 'recorded' ? 'paid' : occurrence.state}`)}
          </span>
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-1">
        {older > 0 && (
          <Button
            variant="ghost"
            className="px-3 text-[13px]"
            disabled={skip.isPending}
            onClick={() => {
              if (
                window.confirm(
                  t('recurring.skipOlderConfirm', { count: older, name: template.name }),
                )
              ) {
                skip.mutate('older');
              }
            }}
          >
            {t('recurring.skipOlder')}
          </Button>
        )}
        <Button
          variant="ghost"
          className="px-3 text-[13px]"
          disabled={skip.isPending}
          onClick={() => {
            if (
              window.confirm(
                t('recurring.skipConfirm', { name: template.name, date: day(occurrence.dueOn) }),
              )
            ) {
              skip.mutate('one');
            }
          }}
        >
          {t('recurring.skip')}
        </Button>
        <Link
          to={recordLink(template, occurrence.dueOn)}
          className="inline-flex min-h-11 items-center rounded-2xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {t('recurring.record')}
        </Link>
      </div>
      <FormError error={error} />
    </div>
  );
}
